import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { trpc, useTRPC } from "../../../shared/api/trpc";

export type FolderDefinition = Awaited<ReturnType<typeof trpc.folders.list.query>>[number];

export function useFolders() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const foldersQuery = useQuery(trpc.folders.list.queryOptions());
  const createMutation = useMutation(trpc.folders.create.mutationOptions());
  const moveMutation = useMutation(trpc.folders.move.mutationOptions());
  const updateMutation = useMutation(trpc.folders.update.mutationOptions());
  const archiveMutation = useMutation(trpc.folders.archive.mutationOptions());
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setError(null);
    try {
      await foldersQuery.refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load folders.");
    }
  }, [foldersQuery.refetch]);

  const applyFolderPatch = useCallback((input: { folderId: string; name: string }) => {
    queryClient.setQueryData<FolderDefinition[]>(trpc.folders.list.queryKey(), (current = []) =>
      current.map((folder) => (folder.id === input.folderId ? { ...folder, name: input.name } : folder)),
    );
  }, [queryClient, trpc]);

  async function createFolder(input: { description: string; name: string; parentFolderId?: string | null; spaceId: string }) {
    setError(null);
    const optimisticId = `optimistic-folder-${crypto.randomUUID()}`;
    const optimisticFolder: FolderDefinition = {
      createdAt: new Date(),
      description: input.description,
      id: optimisticId,
      key: input.name.trim().toLowerCase() || "folder",
      name: input.name.trim(),
      parentFolderId: input.parentFolderId ?? null,
      rank: "zz",
      spaceId: input.spaceId,
      workspaceId: foldersQuery.data?.[0]?.workspaceId ?? "",
    };
    queryClient.setQueryData<FolderDefinition[]>(trpc.folders.list.queryKey(), (current = []) =>
      [...current, optimisticFolder].sort((a, b) => compareOrderKeys(a.rank, b.rank) || a.name.localeCompare(b.name)),
    );
    try {
      const created = await createMutation.mutateAsync(input);
      queryClient.setQueryData<FolderDefinition[]>(trpc.folders.list.queryKey(), (current = []) =>
        current
          .map((folder) => (folder.id === optimisticId ? created : folder))
          .sort((a, b) => compareOrderKeys(a.rank, b.rank) || a.name.localeCompare(b.name)),
      );
      return created;
    } catch (err) {
      queryClient.setQueryData<FolderDefinition[]>(trpc.folders.list.queryKey(), (current = []) => current.filter((folder) => folder.id !== optimisticId));
      setError(err instanceof Error ? err.message : "Could not create folder.");
      throw err;
    }
  }

  async function moveFolder(input: { folderId: string; parentFolderId?: string | null }) {
    setError(null);
    const previousData = foldersQuery.data;
    queryClient.setQueryData<FolderDefinition[]>(trpc.folders.list.queryKey(), (current = []) =>
      current.map((folder) =>
        folder.id === input.folderId
          ? {
              ...folder,
              parentFolderId: input.parentFolderId ?? null,
            }
          : folder,
      ),
    );
    try {
      queryClient.setQueryData(trpc.folders.list.queryKey(), await moveMutation.mutateAsync(input));
    } catch (err) {
      queryClient.setQueryData(trpc.folders.list.queryKey(), previousData);
      setError(err instanceof Error ? err.message : "Could not move folder.");
      throw err;
    }
  }

  function applyTreeOrder(input: { items: Array<{ id: string; kind: "board" | "folder"; rank?: string }>; parentFolderId?: string | null }) {
    const ranksByFolderId = new Map(
      input.items
        .map((item, index) => ({ ...item, rank: item.rank ?? optimisticTreeRank(index) }))
        .filter((item) => item.kind === "folder")
        .map((item) => [item.id, item.rank]),
    );
    if (ranksByFolderId.size === 0) {
      return;
    }
    queryClient.setQueryData<FolderDefinition[]>(trpc.folders.list.queryKey(), (current = []) =>
      current.map((folder) =>
        ranksByFolderId.has(folder.id)
          ? {
              ...folder,
              parentFolderId: input.parentFolderId ?? null,
              rank: requiredMapValue(ranksByFolderId, folder.id),
            }
          : folder,
      ),
    );
  }

  async function updateFolder(input: { folderId: string; name: string }) {
    setError(null);
    const previousData = foldersQuery.data;
    applyFolderPatch(input);
    try {
      queryClient.setQueryData(trpc.folders.list.queryKey(), await updateMutation.mutateAsync(input));
    } catch (err) {
      queryClient.setQueryData(trpc.folders.list.queryKey(), previousData);
      setError(err instanceof Error ? err.message : "Could not update folder.");
      throw err;
    }
  }

  async function archiveFolder(input: { folderId: string }) {
    setError(null);
    const previousData = foldersQuery.data;
    const archivedFolderIds = collectFolderSubtreeIds(foldersQuery.data ?? [], input.folderId);
    queryClient.setQueryData<FolderDefinition[]>(trpc.folders.list.queryKey(), (current = []) => current.filter((folder) => !archivedFolderIds.has(folder.id)));
    try {
      const nextData = await archiveMutation.mutateAsync(input);
      queryClient.setQueryData(trpc.folders.list.queryKey(), nextData);
      return nextData;
    } catch (err) {
      queryClient.setQueryData(trpc.folders.list.queryKey(), previousData);
      setError(err instanceof Error ? err.message : "Could not archive folder.");
      throw err;
    }
  }

  return {
    applyFolderPatch,
    archiveFolder,
    applyTreeOrder,
    createFolder,
    data: foldersQuery.data ?? [],
    error: error ?? (foldersQuery.error instanceof Error ? foldersQuery.error.message : foldersQuery.error ? String(foldersQuery.error) : null),
    isCreating: createMutation.isPending || moveMutation.isPending || updateMutation.isPending || archiveMutation.isPending,
    isLoading: foldersQuery.isPending,
    moveFolder,
    reload,
    updateFolder,
  };
}

function requiredMapValue(map: Map<string, string>, key: string) {
  const value = map.get(key);
  if (!value) {
    throw new Error("Expected tree rank to exist");
  }
  return value;
}

function optimisticTreeRank(index: number) {
  const digits = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
  return `a${digits[index] ?? "z"}`;
}

function compareOrderKeys(first: string, second: string) {
  if (first < second) {
    return -1;
  }
  if (first > second) {
    return 1;
  }
  return 0;
}

function collectFolderSubtreeIds(folders: FolderDefinition[], folderId: string) {
  const ids = new Set<string>([folderId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const folder of folders) {
      if (folder.parentFolderId && ids.has(folder.parentFolderId) && !ids.has(folder.id)) {
        ids.add(folder.id);
        changed = true;
      }
    }
  }
  return ids;
}
